import React, { useState, useEffect, useRef, useContext, useCallback } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from '@material-ui/icons/Group';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, Replay, SwapHoriz } from "@material-ui/icons";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import {
    Avatar,
    ListItemAvatar,
    ListItem,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Paper,
    Divider
} from "@material-ui/core";
import { blue } from "@material-ui/core/colors";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloseIcon from "@material-ui/icons/Close";
import MessageIcon from "@material-ui/icons/Message";

const AI_STRIPE_COLOR = "#00BFA5";
const WAITING_STRIPE_COLOR = "#c4c4c4";

const useStyles = makeStyles((theme) => ({
    ticket: {
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        paddingLeft: theme.spacing(2.25),
        paddingRight: theme.spacing(1),
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25),
        margin: theme.spacing(0.5, 1),
        width: "auto",
        borderRadius: theme.shape.borderRadius,
        transition: "background-color .12s ease-in-out",
        "&:hover": {
            backgroundColor: theme.mode === "light" ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.04)",
        },
        "&:hover $actionsRow": {
            opacity: 1,
            pointerEvents: "auto",
        },
        "&.Mui-selected": {
            backgroundColor: theme.mode === "light"
                ? `${theme.palette.primary.main}14`
                : `${theme.palette.primary.main}26`,
        },
        "&.Mui-selected:hover": {
            backgroundColor: theme.mode === "light"
                ? `${theme.palette.primary.main}1F`
                : `${theme.palette.primary.main}33`,
        },
        [theme.breakpoints.down("sm")]: {
            paddingTop: theme.spacing(1.5),
            paddingBottom: theme.spacing(1.5),
        },
    },

    pendingTicket: {
        cursor: "unset",
    },

    ticketStripe: {
        position: "absolute",
        left: 4,
        top: 10,
        bottom: 10,
        width: 3,
        borderRadius: 2,
    },

    noTicketsDiv: {
        display: "flex",
        height: "100px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    noTicketsText: {
        textAlign: "center",
        color: "rgb(146, 104, 104)",
        fontSize: "14px",
        lineHeight: "1.4",
    },
    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    avatarWrapper: {
        marginRight: theme.spacing(1.5),
        flexShrink: 0,
        marginTop: 2,
    },

    contentWrapper: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
    },

    topRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
    },

    nameGroup: {
        display: "flex",
        alignItems: "center",
        minWidth: 0,
        gap: 4,
    },

    nameText: {
        fontWeight: 600,
        color: theme.mode === "light" ? "#1a1a1a" : "#fff",
    },

    spyIcon: {
        color: blue[700],
        cursor: "pointer",
        flexShrink: 0,
        marginLeft: 12,
        padding: 2,
    },

    timeText: {
        fontSize: "0.72rem",
        color: theme.mode === "light" ? "#667085" : grey[400],
        flexShrink: 0,
        whiteSpace: "nowrap",
    },

    timeTextUnread: {
        fontSize: "0.72rem",
        color: "green",
        fontWeight: "bold",
        flexShrink: 0,
        whiteSpace: "nowrap",
    },

    previewRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        marginTop: 2,
    },

    previewText: {
        color: theme.mode === "light" ? "#4a4a4a" : grey[400],
        fontSize: "0.83rem",
        minWidth: 0,
    },

    previewTextUnread: {
        color: theme.mode === "light" ? "#1a1a1a" : "#fff",
        fontWeight: 700,
        fontSize: "0.83rem",
        minWidth: 0,
    },

    unreadBadge: {
        backgroundColor: green[500],
        color: "#fff",
        borderRadius: 999,
        minWidth: 18,
        height: 18,
        fontSize: "0.68rem",
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 5px",
        flexShrink: 0,
    },

    badgesRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        marginTop: 5,
        alignItems: "center",
    },

    pillBadge: {
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: "0.62rem",
        fontWeight: 700,
        color: "#fff",
        whiteSpace: "nowrap",
        lineHeight: "16px",
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
    },

    aiPillBadge: {
        backgroundColor: AI_STRIPE_COLOR,
    },

    actionsWrap: {
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        marginLeft: 8,
        marginTop: 2,
    },

    actionsRow: {
        display: "flex",
        alignItems: "center",
        opacity: 0,
        pointerEvents: "none",
        transition: "opacity .12s ease-in-out",
        // Corrigido 2026-10-21: esses botoes (transferir/fechar/ignorar)
        // só ficavam visiveis no ":hover" da linha - em touch nao existe
        // hover de verdade, entao no celular eles ficavam permanentemente
        // invisiveis e inacessiveis direto da lista.
        [theme.breakpoints.down("sm")]: {
            opacity: 1,
            pointerEvents: "auto",
        },
    },

    actionIconButton: {
        color: theme.mode === "light" ? "#667085" : "#ccc",
        padding: 6,
        "&:hover": {
            color: theme.mode === "light" ? theme.palette.primary.main : "#fff",
        },
    },

    acceptIconButton: {
        color: "#fff",
        backgroundColor: theme.mode === "light" ? "#0872B9" : theme.palette.primary.main,
        padding: 6,
        "&:hover": {
            backgroundColor: theme.mode === "light" ? "#065a94" : theme.palette.primary.dark,
        },
    },

    dialogTitle: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: theme.palette.primary.main,
        color: "white",
        paddingBottom: theme.spacing(1),
    },
    closeButton: {
        color: "white",
    },
    messagesContainer: {
        height: "60vh",
        maxHeight: "600px",
        overflowY: "auto",
        padding: theme.spacing(2),
        scrollBehavior: "smooth",
    },
    messageItem: {
        padding: theme.spacing(1),
        margin: theme.spacing(1, 0),
        borderRadius: theme.spacing(1),
        maxWidth: "80%",
        position: "relative",
    },
    fromMe: {
        backgroundColor: "#dcf8c6",
        marginLeft: "auto",
    },
    fromThem: {
        backgroundColor: "#f5f5f5",
    },
    messageTime: {
        fontSize: "0.75rem",
        color: grey[500],
        position: "absolute",
        bottom: "2px",
        right: "8px",
    },
    messageText: {
        marginBottom: theme.spacing(2),
        wordBreak: "break-word",
    },
    emptyMessages: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: grey[500],
    },
    messagesHeader: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.grey[100],
    },
    messageAvatar: {
        marginRight: theme.spacing(1),
    },
    messageIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    loadingMessages: {
        display: "flex",
        justifyContent: "center",
        padding: theme.spacing(3),
    }
}));

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
    const classes = useStyles();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
    const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);

    const [openAlert, setOpenAlert] = useState(false);
    const [userTicketOpen, setUserTicketOpen] = useState("");
    const [queueTicketOpen, setQueueTicketOpen] = useState("");
    const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);

    // New states for the ticket messages
    const [ticketMessages, setTicketMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);

    const { ticketId } = useParams();
    const isMounted = useRef(true);
    const { setCurrentTicket } = useContext(TicketsContext);
    const { user } = useContext(AuthContext);

    const { get: getSetting } = useCompanySettings();

    // Ticket ainda sem fila e sem atendente humano assumido - é o sinal
    // que o usuário pediu pra não precisar ficar trocando de aba
    // Atendendo/Aguardando pra descobrir quem está com a IA. Usa só
    // status/queueId/userId (já vêm na listagem) em vez de aiPromptId
    // (que só é setado depois que o classificador identifica um
    // especialista, ou seja, fica de fora bem no começo da conversa,
    // quando só a saudação genérica já foi enviada).
    const isAiHandling = !ticket.isGroup && !ticket.userId && !ticket.queueId
        && (ticket.status === "pending" || ticket.status === "open");

    const stripeColor = isAiHandling
        ? AI_STRIPE_COLOR
        : (ticket.queue?.color || (ticket.queueId ? "#7c7c7c" : WAITING_STRIPE_COLOR));

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
        setAcceptTicketWithouSelectQueueOpen(true);
    }, []);

    const handleCloseTicket = async (id) => {
        const setting = await getSetting(
            {
                "column": "requiredTag"
            }
        );

        if (setting.requiredTag === "enabled") {
            //verificar se tem uma tag
            try {
                if (!ticket.tags || ticket.tags.length === 0) {
                    toast.warning(i18n.t("messagesList.header.buttons.requiredTag"))
                } else {
                    await api.put(`/tickets/${id}`, {
                        status: "closed",
                        userId: user?.id || null,
                    });

                    if (isMounted.current) {
                        setLoading(false);
                    }

                    history.push(`/tickets/`);
                }
            } catch (err) {
                setLoading(false);
                toastError(err);
            }
        } else {
            setLoading(true);
            try {
                await api.put(`/tickets/${id}`, {
                    status: "closed",
                    userId: user?.id || null,
                });

            } catch (err) {
                setLoading(false);
                toastError(err);
            }
            if (isMounted.current) {
                setLoading(false);
            }

            history.push(`/tickets/`);
        }
    };

    const handleCloseIgnoreTicket = async (id) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${id}`, {
                status: "closed",
                userId: user?.id || null,
                sendFarewellMessage: false,
                amountUsedBotQueues: 0,
                isIgnore: true
            });

        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }

        history.push(`/tickets/`);
    };

    const truncate = (str, len) => {
        if (!isNil(str)) {
            if (str.length > len) {
                return str.substring(0, len) + "...";
            }
            return str;
        }
    };

    const handleCloseTransferTicketModal = useCallback(() => {
        if (isMounted.current) {
            setTransferTicketModalOpen(false);
        }
    }, []);

    const handleOpenTransferModal = () => {
        setLoading(true)
        setTransferTicketModalOpen(true);
        if (isMounted.current) {
            setLoading(false);
        }
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
    }

    const handleAcepptTicket = async (id) => {
        setLoading(true);
        try {
            const otherTicket = await api.put(`/tickets/${id}`, ({
                status: ticket.isGroup && ticket.channel === 'whatsapp' ? "group" : "open",
                userId: user?.id,
            }));

            if (otherTicket.data.id !== ticket.id) {
                if (otherTicket.data.userId !== user?.id) {
                    setOpenAlert(true);
                    setUserTicketOpen(otherTicket.data.user.name);
                    setQueueTicketOpen(otherTicket.data.queue.name);
                } else {
                    setLoading(false);
                    setTabOpen(ticket.isGroup ? "group" : "open");
                    handleSelectTicket(otherTicket.data);
                    history.push(`/tickets/${otherTicket.uuid}`);
                }
            } else {
                let setting;

                try {
                    setting = await getSetting({
                        "column": "sendGreetingAccepted"
                    });
                } catch (err) {
                    toastError(err);
                }

                if (setting.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")) {
                    handleSendMessage(ticket.id);
                }
                if (isMounted.current) {
                    setLoading(false);
                }

                setTabOpen(ticket.isGroup ? "group" : "open");
                handleSelectTicket(ticket);
                history.push(`/tickets/${ticket.uuid}`);
            }
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

    const handleSendMessage = async (id) => {
        let setting;

        try {
            setting = await getSetting({
                "column": "greetingAcceptedMessage"
            })
        } catch (err) {
            toastError(err);
        }

        const msg = `${setting.greetingAcceptedMessage}`;
        const message = {
            read: 1,
            fromMe: true,
            mediaUrl: "",
            body: `${msg.trim()}`,
        };
        try {
            await api.post(`/messages/${id}`, message);
        } catch (err) {
            toastError(err);
        }
    };

    const handleCloseAlert = useCallback(() => {
        setOpenAlert(false);
        setLoading(false);
    }, []);

    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setCurrentTicket({ id, uuid, code });
    };

    // Function to fetch messages for the ticket
    const fetchTicketMessages = async (ticketId) => {
        if (!ticketId) return;

        setLoadingMessages(true);
        try {
            const { data } = await api.get(`/messages/${ticketId}`);
            if (isMounted.current) {
                setTicketMessages(data.messages);
            }
        } catch (err) {
            toastError(err);
        } finally {
            setLoadingMessages(false);
        }
    };

    // Handle opening the message dialog
    const handleOpenMessageDialog = (e) => {
        e.stopPropagation();
        setOpenTicketMessageDialog(true);
        fetchTicketMessages(ticket.id);
    };

    return (
        <React.Fragment key={ticket.id}>
            {openAlert && (
                <ShowTicketOpen
                    isOpen={openAlert}
                    handleClose={handleCloseAlert}
                    user={userTicketOpen}
                    queue={queueTicketOpen}
                />
            )}
            {acceptTicketWithouSelectQueueOpen && (
                <AcceptTicketWithouSelectQueue
                    modalOpen={acceptTicketWithouSelectQueueOpen}
                    onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
                    ticketId={ticket.id}
                    ticket={ticket}
                />
            )}
            {transferTicketModalOpen && (
                <TransferTicketModalCustom
                    modalOpen={transferTicketModalOpen}
                    onClose={handleCloseTransferTicketModal}
                    ticketid={ticket.id}
                    ticket={ticket}
                />
            )}

            {/* Improved Message Dialog */}
            <Dialog
                open={openTicketMessageDialog}
                onClose={() => setOpenTicketMessageDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    <Typography variant="h6">
                        Espiando a conversa
                    </Typography>
                    <IconButton
                        aria-label="close"
                        className={classes.closeButton}
                        onClick={() => setOpenTicketMessageDialog(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <div className={classes.messagesHeader}>
                    <Avatar
                        src={ticket?.contact?.urlPicture}
                        className={classes.messageAvatar}
                    />
                    <div>
                        <Typography variant="subtitle1">
                            {ticket.contact?.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {ticket.whatsapp?.name || ticket.channel}
                        </Typography>
                    </div>
                </div>

                <Divider />

                <DialogContent className={classes.messagesContainer}>
                    {loadingMessages ? (
                        <div className={classes.loadingMessages}>
                            <Typography>Carregando mensagens...</Typography>
                        </div>
                    ) : ticketMessages.length === 0 ? (
                        <div className={classes.emptyMessages}>
                            <MessageIcon fontSize="large" />
                            <Typography variant="body1">
                                {i18n.t("ticketsList.noMessages")}
                            </Typography>
                        </div>
                    ) : (
                        ticketMessages.map((message) => (
                            <Paper
                                key={message.id}
                                className={clsx(
                                    classes.messageItem,
                                    message.fromMe ? classes.fromMe : classes.fromThem
                                )}
                                elevation={0}
                            >
                                <Typography className={classes.messageText}>
                                    {message.body.includes('data:image/png;base64') ? (
                                        <MarkdownWrapper>Localização</MarkdownWrapper>
                                    ) : message.body.includes('BEGIN:VCARD') ? (
                                        <MarkdownWrapper>Contato</MarkdownWrapper>
                                    ) : (
                                        <MarkdownWrapper>{message.body}</MarkdownWrapper>
                                    )}
                                </Typography>
                                <Typography variant="caption" className={classes.messageTime}>
                                    {format(parseISO(message.createdAt), "HH:mm")}
                                </Typography>
                            </Paper>
                        ))
                    )}
                </DialogContent>
            </Dialog>

            <ListItem
                button
                dense
                onClick={(e) => {
                    const isCheckboxClicked = (e.target.tagName.toLowerCase() === 'input' && e.target.type === 'checkbox')
                        || (e.target.tagName.toLowerCase() === 'svg' && e.target.type === undefined)
                        || (e.target.tagName.toLowerCase() === 'path' && e.target.type === undefined);
                    // Se o clique foi no Checkbox, não execute handleSelectTicket

                    if (isCheckboxClicked) return;

                    handleSelectTicket(ticket);
                }}
                selected={ticketId && ticketId === ticket.uuid}
                className={clsx(classes.ticket, {
                    [classes.pendingTicket]: ticket.status === "pending",
                })}
            >
                <span className={classes.ticketStripe} style={{ backgroundColor: stripeColor }} />

                <ListItemAvatar className={classes.avatarWrapper} style={{ margin: 0, minWidth: "auto" }}>
                    <Avatar
                        style={{
                            width: "44px",
                            height: "44px",
                            borderRadius: "50%",
                        }}
                        src={`${ticket?.contact?.urlPicture}`}
                    />
                </ListItemAvatar>

                <div className={classes.contentWrapper}>
                    <div className={classes.topRow}>
                        <div className={classes.nameGroup}>
                            {ticket.isGroup && ticket.channel === "whatsapp" && (
                                <GroupIcon fontSize="small" style={{ color: grey[700] }} />
                            )}
                            {ticket.channel && (
                                <ConnectionIcon width="18" height="18" connectionType={ticket.channel} />
                            )}
                            <Typography noWrap component="span" variant="body2" className={classes.nameText}>
                                {truncate(ticket.contact?.name, 40)}
                            </Typography>
                            <Tooltip title="Espiar Conversa">
                                <VisibilityIcon
                                    onClick={handleOpenMessageDialog}
                                    fontSize="small"
                                    className={classes.spyIcon}
                                />
                            </Tooltip>
                        </div>

                        {ticket.lastMessage && (
                            <Typography
                                component="span"
                                className={Number(ticket.unreadMessages) > 0 ? classes.timeTextUnread : classes.timeText}
                            >
                                {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                                    format(parseISO(ticket.updatedAt), "HH:mm")
                                ) : (
                                    format(parseISO(ticket.updatedAt), "dd/MM/yyyy")
                                )}
                            </Typography>
                        )}
                    </div>

                    <div className={classes.previewRow}>
                        <Typography
                            noWrap
                            component="span"
                            className={Number(ticket.unreadMessages) > 0 ? classes.previewTextUnread : classes.previewText}
                        >
                            {ticket.lastMessage ? (
                                ticket.lastMessage.includes('fb.me') ? (
                                    <MarkdownWrapper>Clique de Anúncio</MarkdownWrapper>
                                ) : ticket.lastMessage.includes('data:image/png;base64') ? (
                                    <MarkdownWrapper>Localização</MarkdownWrapper>
                                ) : ticket.lastMessage.includes('BEGIN:VCARD') ? (
                                    <MarkdownWrapper>Contato</MarkdownWrapper>
                                ) : (
                                    <MarkdownWrapper>{truncate(ticket.lastMessage, 45)}</MarkdownWrapper>
                                )
                            ) : (
                                <>&nbsp;</>
                            )}
                        </Typography>

                        {Number(ticket.unreadMessages) > 0 && (
                            <span className={classes.unreadBadge}>{ticket.unreadMessages}</span>
                        )}
                    </div>

                    <div className={classes.badgesRow}>
                        {isAiHandling && (
                            <Tooltip title="A IA está conduzindo essa conversa, ninguém assumiu ainda">
                                <span className={clsx(classes.pillBadge, classes.aiPillBadge)}>
                                    <SmartToyIcon style={{ fontSize: 11 }} /> IA
                                </span>
                            </Tooltip>
                        )}
                        {ticket?.whatsapp && (
                            <span
                                className={classes.pillBadge}
                                style={{ backgroundColor: ticket.channel === "whatsapp" ? "#25D366" : ticket.channel === "facebook" ? "#4267B2" : "#E1306C" }}
                            >
                                {ticket.whatsapp?.name.toUpperCase()}
                            </span>
                        )}
                        <span className={classes.pillBadge} style={{ backgroundColor: ticket.queue?.color || "#7c7c7c" }}>
                            {ticket.queueId ? ticket.queue?.name.toUpperCase() : ticket.status === "lgpd" ? "LGPD" : "SEM FILA"}
                        </span>
                        {ticket?.user && (
                            <span className={classes.pillBadge} style={{ backgroundColor: "#000000" }}>
                                {ticket.user?.name.toUpperCase()}
                            </span>
                        )}
                        {ticket.tags?.map((tag) => (
                            <ContactTag tag={tag} key={`ticket-contact-tag-${ticket.id}-${tag.id}`} />
                        ))}
                    </div>
                </div>

                <div className={classes.actionsWrap}>
                    {(ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined)) && (
                        <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                            <IconButton
                                size="small"
                                className={classes.acceptIconButton}
                                onClick={(e) => { e.stopPropagation(); handleOpenAcceptTicketWithouSelectQueue(); }}
                                disabled={loading}
                            >
                                <Done fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {(ticket.status === "pending" && ticket.queueId !== null) && (
                        <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                            <IconButton
                                size="small"
                                className={classes.acceptIconButton}
                                onClick={(e) => { e.stopPropagation(); handleAcepptTicket(ticket.id); }}
                                disabled={loading}
                            >
                                <Done fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {(ticket.status === "closed" && (ticket.queueId === null || ticket.queueId === undefined)) && (
                        <Tooltip title={`${i18n.t("ticketsList.buttons.reopen")}`}>
                            <IconButton
                                size="small"
                                className={classes.acceptIconButton}
                                onClick={(e) => { e.stopPropagation(); handleOpenAcceptTicketWithouSelectQueue(); }}
                                disabled={loading}
                            >
                                <Replay fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {(ticket.status === "closed" && ticket.queueId !== null) && (
                        <Tooltip title={`${i18n.t("ticketsList.buttons.reopen")}`}>
                            <IconButton
                                size="small"
                                className={classes.acceptIconButton}
                                onClick={(e) => { e.stopPropagation(); handleAcepptTicket(ticket.id); }}
                                disabled={loading}
                            >
                                <Replay fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}

                    <div className={classes.actionsRow}>
                        {(ticket.status === "pending" || ticket.status === "open" || ticket.status === "group") && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                                <IconButton
                                    size="small"
                                    className={classes.actionIconButton}
                                    onClick={(e) => { e.stopPropagation(); handleOpenTransferModal(); }}
                                    disabled={loading}
                                >
                                    <SwapHoriz fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {(ticket.status === "open" || ticket.status === "group") && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.closed")}`}>
                                <IconButton
                                    size="small"
                                    className={classes.actionIconButton}
                                    onClick={(e) => { e.stopPropagation(); handleCloseTicket(ticket.id); }}
                                    disabled={loading}
                                >
                                    <HighlightOff fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {((ticket.status === "pending" || ticket.status === "lgpd") && (user.userClosePendingTicket === "enabled" || user.profile === "admin")) && (
                            <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                                <IconButton
                                    size="small"
                                    className={classes.actionIconButton}
                                    onClick={(e) => { e.stopPropagation(); handleCloseIgnoreTicket(ticket.id); }}
                                    disabled={loading}
                                >
                                    <HighlightOff fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </ListItem>
        </React.Fragment>
    );
};

export default TicketListItemCustom;
